// Mock data for CNESS B2B Landing Page - Updated with official content

export const mockData = {
  hero: {
    title: "Show the world your business stands for more.",
    subtitle: "A credible, human-centered certification that proves you operate with integrity and measurable impact—without slowing growth.",
    quickProofIcons: [
      "Trusted Framework",
      "Tiered Badges", 
      "Fast Onboarding",
      "Assessment + Evidence",
      "Public Recognition"
    ],
    primaryCta: "Get Certified",
    secondaryCta: "Compare Tiers", 
    tertiaryCta: "Explore the Pillars",
    altText: "Diverse leadership team reviewing purpose driven performance metrics."
  },
  whyCertification: {
    kicker: "Why Certification",
    title: "Show the world your business stands for more.",
    body: "In markets that reward trust, certification is currency. CNESS validates the practices behind your promises—how you treat people, steward the planet, protect data, and create lasting value. Your badge signals maturity, not marketing.",
    cta: "Get Certified"
  },

  benefits: {
    title: "Benefits of CNESS Certification",
    cta: "Get Certified", 
    benefits: [
      {
        title: "Build Trust with Customers",
        description: "Transparent standards and independent validation reassure buyers you deliver what you claim—ethically and consistently."
      },
      {
        title: "Enhance Brand Reputation", 
        description: "Stand out with a badge that recognises integrity, accountability, and verifiable impact across the enterprise."
      },
      {
        title: "Attract and Retain Talent",
        description: "Today's workforce chooses employers who act with purpose. Certification helps you prove it—and keep it."
      },
      {
        title: "Win Investor & Partner Confidence",
        description: "A credible lens on governance, risk, and resilience. Reduce diligence friction and unlock stronger alliances."
      },
      {
        title: "Improve Internal Operations",
        description: "A structured path to elevate processes, documentation, and outcomes—turning good intent into repeatable excellence."
      }
    ]
  },

  howItWorks: {
    title: "A practical pathway—discover, assess, achieve.",
    steps: [
      {
        id: 1,
        title: "Discover (Eligibility)",
        description: "Quick guided check to confirm readiness and map your likely tier.",
        ctas: ["Check Eligibility"]
      },
      {
        id: 2, 
        title: "Assess (Qual + Quant + Evidence)",
        description: "Guided inputs, attach evidence, and submit best practices—backed by clear scoring.",
        ctas: ["Compare Tiers"]
      },
      {
        id: 3,
        title: "Achieve (Badge & Recognition)", 
        description: "Receive your tier, display your digital badge, and appear in the CNESS Directory.",
        ctas: ["See Pricing", "Start Now"]
      }
    ]
  },
  
  pillars: {
    title: "The CNESS Framework—what we measure and why.",
    subtitle: "Built on 6 Pillars of Conscious Enterprise",
    description: "Our framework assesses organisational practices across six essential dimensions of conscious business operations.",
    image: "/six_pillars_chart.png",
    cta: "Explore the Pillars",
    pillars: [
      {
        id: 1,
        title: "Conscious Leadership",
        description: "Responsible decision-making, accountability and leadership aligned with conscious business principles.",
        icon: "Shield"
      },
      {
        id: 2,
        title: "Human-Centric Culture & Team Cohesion",
        description: "A people-first workplace where teams feel valued, connected, supported and aligned on purpose.",
        icon: "Heart"
      },
      {
        id: 3,
        title: "Products & Services",
        description: "Offerings that are useful, responsible and designed to create meaningful value.",
        icon: "Users"
      },
      {
        id: 4,
        title: "Customers & Clients",
        description: "Trust, care, transparency and long-term relationships with those the business serves.",
        icon: "Lightbulb"
      },
      {
        id: 5,
        title: "Community & Conscious Social Impact",
        description: "Contribution beyond profit through community connection and responsible growth.",
        icon: "Leaf"
      },
      {
        id: 6,
        title: "Brand Vision & Collaboration",
        description: "A clear brand purpose, authentic voice and partnerships that build a conscious marketplace.",
        icon: "Eye"
      }
    ]
  },

  certificationTiers: {
    title: "Choose the tier that matches your maturity.",
    subtitle: "Three certification levels designed to recognize your organization's conscious business journey",
    note: "Tiers are sector and size aware (Micro, Small, Medium, Large, Enterprise) and evolve with your progress.",
    ctas: ["Compare Tiers", "See Pricing", "Get Started"],
    tiers: [
        {
        id: "aspiring",
        name: "Aspiring",
        badge: "Entry",
        description: "Baseline ethical and operational criteria with documented intent to improve.",
        badgeImage: "https://customer-assets.emergentagent.com/job_premier-b2b-site/artifacts/96hhy209_Badge%20Level%201.png",
        features: [
          "Basic assessment and evaluation",
          "Foundation-level certification", 
          "Access to essential resources",
          "Community support",
          "Digital badge and recognition"
        ],
        eligibility: "Organizations beginning their conscious journey",
        popular: false
      },
      {
        id: "inspired", 
        name: "Inspired",
        badge: "Advanced",
        description: "Strong maturity across pillars with evidence, consistent results, and active best practice sharing.",
        badgeImage: "https://customer-assets.emergentagent.com/job_premier-b2b-site/artifacts/r11ziiyu_Badge%20Level%202.png",
        features: [
          "Comprehensive assessment",
          "Advanced certification",
          "Mentorship opportunities", 
          "Best practice sharing platform",
          "Priority support",
          "Directory listing with enhanced profile"
        ],
        eligibility: "Organizations with demonstrated conscious practices",
        popular: true
      },
      {
        id: "luminary",
        name: "Luminary", 
        badge: "Leadership",
        description: "Exemplary performance, peer recognised practices, and measurable, sustained impact at scale.",
        badgeImage: "https://customer-assets.emergentagent.com/job_premier-b2b-site/artifacts/e1lw588c_Badge%20Level%203.png",
        features: [
          "Expert-level assessment",
          "Leadership certification",
          "Thought leadership platform",
          "Mentor program participation",
          "Strategic partnership opportunities",
          "Exclusive networks access",
          "Featured directory placement"
        ],
        eligibility: "Industry leaders in conscious business practices",
        popular: false
      }
    ]
  },

  pricing: {
    title: "Simple plans. Size-aware pricing. No surprises.",
    subtitle: "Pricing is calibrated to organization size and sector, covering assessments, verification, and certification assets. See what's included at each tier and plan with confidence.",
    bullets: [
      "Assessment Scope",
      "Reviewer Time", 
      "Badge Kit & Assets",
      "Directory Recognition",
      "Renewal Pathway"
    ],
    ctas: ["See Pricing", "Start Now"]
  },

  mentorPartner: {
    title: "Bring your expertise. Amplify your impact.",
    mentors: {
      title: "Mentors",
      description: "Guide organisations through assessments and improvements; receive recognition inside the ecosystem."
    },
    partners: {
      title: "Partners", 
      description: "Collaborate on programmes, training, and scale up initiatives aligned to the 6 Pillars."
    },
    ctas: ["Become a Mentor", "Become a Partner"]
  },

  bestPracticeHub: {
    title: "Share what works. Learn what's next.",
    description: "A living library of field tested practices from certified organisations—searchable by pillar, sector, and outcome. Submit your own and credit your team for raising the bar.",
    ctas: ["Explore Best Practices", "Submit Yours"]
  },

  certifiedDirectory: {
    title: "Find—and be found—by values aligned leaders.",
    description: "Browse verified organisations by tier, size, sector, and region. Every listing includes a badge, profile snapshot, and contact safe visibility that respects privacy.",
    ctas: ["Browse Directory", "View Profile"]
  },

  trustProof: {
    title: "Trusted by organisations committed to people, planet, and performance.",
    quote: {
      text: "Certification clarified our priorities and accelerated execution."
    }
  },

  footer: {
    columns: [
      {
        title: "Support & Legal",
        links: [
          { label: "Contact Us", url: "/support" },
          { label: "FAQ", url: "/support" },
          { label: "Terms & Conditions", url: "/terms" },
          { label: "Privacy Policy", url: "/privacy" },
          { label: "Help Center", url: "/support" }
        ]
      },
      {
        title: "Media & Visibility", 
        links: [
          { label: "Press Room", url: "/press" },
          { label: "News & Updates", url: "/news" },
          { label: "Media Kit", url: "/media-kit" },
          { label: "Newsletter", url: "/newsletter" }
        ]
      },
      {
        title: "Discover",
        links: [
          { label: "About CNESS", url: "/about" },
          { label: "6 Pillars Deep Dive", url: "/pillars" },
          { label: "Events", url: "/events" }
        ]
      },
      {
        title: "Ecosystem",
        links: [
          { label: "Mentor Program", url: "/mentor-partner" },
          { label: "Partner Program", url: "/mentor-partner" },
          { label: "Community Forum", url: "/community" }
        ]
      }
    ],
    socialMedia: [
      { platform: "Facebook", url: "https://www.facebook.com/profile.php?id=61567141033015", icon: "Facebook" },
      { platform: "Instagram", url: "https://www.instagram.com/cness.inc?igsh=cHR2NXRtNWtheWlu", icon: "Instagram" },
      { platform: "YouTube", url: "https://youtube.com/@cnessinc?si=szCT4h4ymcPe4a6G", icon: "Youtube" },
      { platform: "LinkedIn", url: "https://www.linkedin.com/company/cness-inc/", icon: "Linkedin" }
    ],
    copyright: "© 2026 CNESS INC. All rights reserved."
  },

  navigation: {
    logo: {
      text: "CNESS",
      url: "/"
    },
    items: [
      { label: "About CNESS", url: "/about" },
      { label: "Certification", url: "/certification" },
      { label: "6 Pillars", url: "/pillars" },
      { label: "Mentor/Partner", url: "/mentor-partner" },
      { label: "Support", url: "/support" },
      { label: "Login", url: "/login", type: "secondary" },
      { label: "Sign Up", url: "/signup", type: "primary" }
    ]
  }
};